/** Accept only canonical decimal encodings of uint256 identities. */
export function parseCanonicalAgentId(value: unknown): string | null {
  if (typeof value !== "string" || !/^(?:0|[1-9][0-9]{0,77})$/.test(value)) return null;
  return BigInt(value) < 2n ** 256n ? value : null;
}

/** Allowed clock skew for observations and event times, anchored to the observation. */
export const RUNTIME_CLOCK_SKEW_MS = 5000;

export type RuntimeView = {
  phase: "awaiting_bridge" | "bridge_pending" | "accepted" | "rejected" | "observed" | "unknown";
  reportedStatus: string | null;
  freshness: "unknown" | "fresh" | "stale" | "final";
  startedAtMs: number | null;
  expiresAtMs: number | null;
  deletedAtMs: number | null;
  observedAtMs: number | null;
};

export type RuntimePresentation = { active: boolean; label: string; freshness: RuntimeView["freshness"] };

/** Describe reported hosting state from the server-time anchor; never infer liveness from chain history. */
export function runtimePresentation(runtime: RuntimeView | null, serverNowMs: number): RuntimePresentation {
  const result = (active: boolean, label: string, freshness: RuntimeView["freshness"]): RuntimePresentation => ({ active, label, freshness });
  if (!runtime) return result(false, "hosting status unavailable", "unknown");
  if (runtime.reportedStatus === "deleted" && runtime.deletedAtMs !== null) return result(false, "deleted", "final");
  if (runtime.phase === "rejected") return result(false, "bridge rejected", "unknown");
  if (runtime.phase === "awaiting_bridge" || runtime.phase === "bridge_pending") return result(false, "awaiting bridge", "unknown");
  if (runtime.phase === "unknown") return result(false, "hosting status unavailable", "unknown");
  if (runtime.phase === "accepted" || !runtime.observedAtMs) return result(false, "hosting status unknown", "unknown");
  const activeReport = ["ready", "busy", "idle"].includes(runtime.reportedStatus ?? "");
  const windowValid = Number.isSafeInteger(runtime.startedAtMs) && Number.isSafeInteger(runtime.expiresAtMs)
    && runtime.startedAtMs! > 0 && runtime.expiresAtMs! > runtime.startedAtMs!
    && runtime.expiresAtMs! - runtime.startedAtMs! <= 172800000
    && runtime.startedAtMs! <= serverNowMs + RUNTIME_CLOCK_SKEW_MS;
  if (activeReport && (!windowValid || !Number.isSafeInteger(runtime.observedAtMs) || runtime.observedAtMs! > serverNowMs + RUNTIME_CLOCK_SKEW_MS)) {
    return result(false, "hosting status unknown", "unknown");
  }
  if (runtime.expiresAtMs !== null && serverNowMs >= runtime.expiresAtMs) return result(false, "expiry reached · cleanup pending", runtime.freshness);
  const age = serverNowMs - runtime.observedAtMs;
  const freshness = age < -RUNTIME_CLOCK_SKEW_MS ? "unknown" : age > 60000 ? "stale" : runtime.freshness;
  if (freshness === "stale") return result(false, "hosting status stale", "stale");
  if (freshness === "unknown") return result(false, "hosting status unknown", "unknown");
  if (["ready", "busy", "idle"].includes(runtime.reportedStatus ?? "")) return result(true, `hosting ${runtime.reportedStatus}`, freshness);
  const labels: Record<string, string> = {
    reserved: "reservation pending", reservation_aborting: "reservation cleanup pending", queued: "hosting queued",
    provisioning: "hosting starting", stopped: "hosting stopped", failed: "hosting failed",
    ownership_ambiguous: "ownership needs review", expired: "expiry reached · cleanup pending",
    deleting: "deletion pending", deletion_failed: "deletion needs retry",
  };
  return result(false, labels[runtime.reportedStatus ?? ""] ?? "hosting status unknown", freshness);
}
